import { Arg, CONTENT_ARGS, FLEX_ARGS, Metadata, pick } from '../../../utils/spds/metadata';
import { CENTER, INITIAL, SPACE_BETWEEN } from '../../../utils/spds/constants/css-layout';
import { not, or } from 'ember-truth-helpers';
import Body from './empty-state/body.gjs';
import FlexColumn from '../layout/flex/column.gjs';
import FlexRow from '../layout/flex/row.gjs';
import Super from '../core/base.gjs';
import Variable from '../layout/variable.gjs';
import styles from './empty-state.module.scss';
import svgJar from 'ember-svg-jar/helpers/svg-jar';

const METADATA = Metadata.define({
  description: `
Visual container used when a list, table, or chart has no items or data to show.

### Required arguments
\`@title\` and \`@illustration\` are always required. \`@subtitle\` and \`@advancedCaption\` are only required if \`@advanced\` is set to \`true\`. \`@body\` is required if the \`:body\` named block is not being used.

### Named blocks
This component exposes the following named blocks.

#### \`:body\`
Used for the main content of the empty state message. Allows for the inclusion of more complex content than simple text, such as links.
If you only need to include text content, then the \`@body\` argument is preferred.

#### \`:cta\`
Used to add a CTA button to the empty state.
  `,
  extends: [
    pick(CONTENT_ARGS, [
      {
        name: 'subtitle',
        description: 'Subtitle text displayed below the title (advanced variant only)',
        initialValue: 'Subtitle goes here',
      },
      {
        name: 'title',
        description: 'Main heading text for the empty state',
        initialValue: 'Title goes here',
        required: true,
      },
    ]),
    pick(FLEX_ARGS, [
      {
        name: 'alignItems',
        description: 'CSS align-items for the layout and text containers',
        defaultWhen: ['advanced', { true: INITIAL, false: CENTER }],
      },
      {
        name: 'justifyContent',
        description: 'CSS justify-content for the outer layout container',
        defaultWhen: ['advanced', { true: SPACE_BETWEEN, false: CENTER }],
      },
    ]),
  ],
  meta: {
    advanced: Arg.boolean({
      default: false,
      description: 'Enables the advanced variant with horizontal layout and additional text fields',
    }),
    advancedCaption: Arg.string({
      description: 'Caption text displayed below the CTA area (advanced variant only)',
    })
      .initial(
        'Amet minim mollit non deserunt ullamco est sit aliqua dolor do amet sint. Velit officia consequat duis enim velit mollit.'
      )
      .requires([
        {
          name: 'advanced',
          reason: 'Caption is only displayed in the advanced variant',
        },
      ]),
    body: Arg.string({ description: 'Body text content' }).initial(
      'Amet minim mollit non deserunt ullamco est sit aliqua dolor do amet sint. Velit officia consequat duis enim velit mollit.'
    ),
    illustration: Arg.string({ description: 'SVG illustration name to display' })
      .initial('calendar-empty-state')
      .required(),
  },
  name: 'Empty State',
  resources: {
    zeroheight: 'https://zeroheight.com/3ddd0f892/p/23f450-empty-state',
  },
});

export default class SpdsFeedbackEmptyState extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);
  baseClass = 'spds-feedback-empty-state';
  classesToMap = ['advanced'];
  styles = styles;

  get advanced() {
    return this.argOrDefault('advanced');
  }

  get alignItems() {
    return this.argOrDefault('alignItems');
  }

  get justifyContent() {
    return this.argOrDefault('justifyContent');
  }

  <template>
    <Variable
      @alignItems={{this.alignItems}}
      @column={{not this.advanced}}
      @gap="24px"
      @justifyContent={{this.justifyContent}}
      @reverse={{this.advanced}}
      class={{this.classes}}
      data-test-spds-feedback-empty-state
      ...attributes
      {{this.validateArguments}}
    >
      <FlexRow
        @alignItems="center"
        @flex="1"
        @justifyContent="center"
        @tag="figure"
        class="illustration-container"
        data-test-spds-feedback-empty-state-illustration-container
      >
        {{svgJar @illustration class="illustration"}}
      </FlexRow>
      <FlexColumn
        @flex="1"
        @gap="24px"
        @justifyContent="center"
        class="body"
        data-test-spds-feedback-empty-state-body-container
      >
        <FlexColumn
          @alignItems={{this.alignItems}}
          @gap="4px"
          @justifyContent="center"
          class="text-container"
          data-test-spds-feedback-empty-state-text-container
        >
          <FlexRow @tag="h2" class="title" data-test-spds-feedback-empty-state-title>
            {{@title}}
          </FlexRow>
          {{#if @advanced}}
            <FlexRow @tag="p" class="subtitle" data-test-spds-feedback-empty-state-subtitle>
              {{@subtitle}}
            </FlexRow>
          {{/if}}
          <FlexRow @tag="p" class="body-text" data-test-spds-feedback-empty-state-body-text>
            {{#if (has-block "body")}}
              {{yield to="body"}}
            {{else}}
              <Body @body={{@body}} />
            {{/if}}
          </FlexRow>
        </FlexColumn>
        {{#if (or (has-block "cta") @advanced)}}
          <FlexColumn
            @alignItems={{this.alignItems}}
            @gap="24px"
            @justifyContent="center"
            class="cta-container"
            data-test-spds-feedback-empty-state-cta-container
          >
            {{yield to="cta"}}
            {{#if @advanced}}
              <p class="advanced-caption" data-test-spds-feedback-empty-state-advanced-caption>
                {{@advancedCaption}}
              </p>
            {{/if}}
          </FlexColumn>
        {{/if}}
      </FlexColumn>
    </Variable>
  </template>
}

export const EMPTY_STATE_METADATA = SpdsFeedbackEmptyState.metadata;
